import styled, {css, useTheme} from "styled-components";
import HomepageCardTitle from "../atoms/HomepageCardTitle";
import NewContentBadge from "../atoms/NewContentBadge";
import __ from "../../utils/translator";
import HomepageCardManualsList from "../molecules/HomepageCardManualsList";
import themeResponsiveProperty from "../responsive/themeResponsiveProperty";
import HomepageCardThemesList from "../molecules/HomepageCardThemesList";
import HomepageTypeIcon from "../atoms/HomepageTypeIcon";
import HomepageCardActionButton from "../atoms/HomepageCardActionButton";
import RenderOnTablet from "../responsive/RenderOnTablet";
import LinkButton from "../LinkButton";
import {withRouter} from "react-router-dom";
import JwtStore from "../../flux/JwtStore";
import {TermTitle} from "theme-builder/dist/theme-builder";
import HomeStoreActions from "../../flux/HomeStoreActions";
import HomepageCardCompletionBar from "../atoms/HomepageCardCompletionBar";

export const typologies = {
  seasonal: 'seasonal',
  merchandisingManual: 'merchandising-manual',
  timeless: 'timeless',
  productFocus: 'product-focus',
  glossary: 'glossary'
}

function HomepageCard({
  history,
  fullscreen = false,
  isNew = false,
  confidential = false,
  visibleOnlyForOutlets = false,
  id,
  name = "",
  typology = null,
  cover = "",
  data = {},
  completionPercentage = 0,
  manualsNeverSeen
}) {
  const appTheme = useTheme();

  const openTermsModal = (event) => {
    event.stopPropagation();
    if (typology === typologies.glossary) {
      HomeStoreActions.openTermModal([data.glossaryTerm]);
    } else {
      HomeStoreActions.openTermModal(data.terms);
    }
    return false;
  }

  const goToDetailsPage = (event) => {
    event.stopPropagation();
    switch (typology) {
      case 'seasonal':
        history.push(`/seasonal/${id}`);
        break;
      case 'timeless':
        history.push(`/timeless/${data.parentId}/themes/${id}`);
        break;
      case 'product-focus':
        history.push(`/product-focuses/${id}`);
        break;
      case 'merchandising-manual':
        history.push(`/seasonal/${id}/manuals`);
        break;
      case 'glossary':
        openTermsModal(event);
        break;
      default:
        return false;
    }
    return false;
  }

  const hasCompletionPercentage = completionPercentage !== 0 && isNew === false;

  return (
    <CardContainer
      typology={typology}
      $fullscreen={fullscreen}
      cover={cover === null && typology === typologies.glossary ? '/images/image_default_glossary.png': cover}
      onClick={goToDetailsPage}
    >
      <CardBlock>
        <BadgesContainer>
          {isNew && <NewContentBadge>{__('Never seen')}</NewContentBadge>}
          {visibleOnlyForOutlets && <Badge>{__('Outlet Only')}</Badge>}
          {confidential && (
            <Badge style={{
              backgroundColor: 'rgb(204, 22, 22)'
            }}>{__('draft')}</Badge>
          )}
        </BadgesContainer>
        {data.terms !== undefined && data.terms.length > 0 && typology === typologies.seasonal && (
          <RenderOnTablet>
            <div style={{
              position: 'absolute',
              inset: '1.25rem 1.25rem auto auto',
            }}>
              <HomepageCardActionButton onClick={openTermsModal}>
                <HomepageTypeIcon name="terms"/>
                <span>+{data.terms.length} {__('terms')}</span>
              </HomepageCardActionButton>
            </div>
          </RenderOnTablet>
        )}
        <HomepageCardTitle
          typology={typology}
        >
          {typology === typologies.glossary ? (
            <TermTitleContainer>
              <TermTitle
                awsCredentials={JwtStore.getAwsCredentials()}
                term={data.glossaryTerm}
              />
            </TermTitleContainer>
          ) : name}
        </HomepageCardTitle>

        {typology === typologies.seasonal && (
          <RenderOnTablet>
            <CollectionCallToAction size="large" to={`/seasonal/${id}`}>
              {__('Start now')}
            </CollectionCallToAction>
          </RenderOnTablet>
        )}

        <ContentContainer>
          {(typology === typologies.merchandisingManual && data.manuals) && (
            <HomepageCardManualsList
              neverSeen={manualsNeverSeen}
              parentId={id}
              manuals={data.manuals}
            />
          )}
          {(typology === typologies.seasonal && data.themes) && (
            <HomepageCardThemesList
              manualsNeverSeen={manualsNeverSeen}
              collection={{id, name, cover}}
              themes={data.themes}
              manuals={data.manuals}
            />
          )}
          {(appTheme.isMobile || typology !== typologies.seasonal) && (
            <BottomActions>
              {(typology !== typologies.glossary && typology !== typologies.merchandisingManual) && (
                <>
                  {data.terms !== undefined && data.terms.length > 0 && (
                    <HomepageCardActionButton onClick={openTermsModal}>
                      <HomepageTypeIcon name="terms"/>
                      <span>+{data.terms.length} {__('terms')}</span>
                    </HomepageCardActionButton>
                  )}
                  {data.manuals !== undefined && data.manuals.length > 0 && (
                    <HomepageCardActionButton onClick={(event) => {
                      event.stopPropagation();
                      history.push(`/seasonal/${id}/manuals`);
                      return false;
                    }}>
                      <HomepageTypeIcon name={typologies.merchandisingManual}/>
                      <span>+{data.manuals.length} {__('manuals')}</span>
                    </HomepageCardActionButton>
                  )}
                </>
              )}
            </BottomActions>
          )}
          {typology !== typologies.seasonal && (
            <CompletionBarContainer>
              {hasCompletionPercentage && (
                <HomepageCardCompletionBar
                  percentage={completionPercentage}
                />
              )}
            </CompletionBarContainer>
          )}
        </ContentContainer>
      </CardBlock>
    </CardContainer>
  )
}

export default withRouter(HomepageCard);

const CardContainer = styled.div`
  ${props => props.$fullscreen ? css`
    width: ${themeResponsiveProperty('100%', '56.6875rem')};
  `: css`
    width: 100%;
  `}
  height: ${props => themeResponsiveProperty(
    props.typology === typologies.seasonal ? '25.81rem' : '18.75rem',
    props.typology === typologies.seasonal ? '37.25rem' : '31.3rem'
  )};
  position: relative;
  border-radius: ${(props) => props.$fullscreen ? 'none' : '.5rem'};
  ${(props) => props.theme.isTablet && css`
    border-radius: .5rem;
  `}
  overflow: hidden;
  background: linear-gradient(0deg, #000 12.64%, rgba(0, 0, 0, 0.00) 50%),
    linear-gradient(0deg, rgba(0, 0, 0, 0.20) 0%,
    rgba(0, 0, 0, 0.20) 100%),
    url("${(props) => props.cover}"),
    lightgray 50% / cover no-repeat;

  background-size: cover;
  background-repeat: no-repeat;
  background-position: center center;
  display: flex;
  flex-direction: column;
  font-family: 'Din-Light', sans-serif;
`;

const CardBlock = styled.div`
  width: 100%;
  flex: 1;
  height: 100%;
  display: flex;
  flex-direction: column;
`;

const ContentContainer = styled.div`
  box-sizing: border-box;
  background: ${themeResponsiveProperty('#000', 'transparent')};
  display: flex;
  flex-direction: column;
  align-items: end;
  padding-bottom: .5rem;
`;

const BottomActions = styled.div`
  display: flex;
  flex-direction: row;
  gap: .5rem;
  width: 100%;
  padding: 0 1rem;

  button {
    margin: .5rem 0 1rem;
  }
`;

const CollectionCallToAction = styled(LinkButton)`
  margin: 1rem 4rem;
  display: inline;
  padding: 0.75rem 2.125rem 0.75rem;
  width: fit-content;
  font-family: 'Din-Medium', sans-serif;
`;

const TermTitleContainer = styled.div`
  color: #FFF;
  width: 100%;
  line-height: 1;

  button {
    padding: 0;
  }

  svg{
    color: #FFF !important;
  }

  span {
    font-size: 0.875rem;
  }

  h4 {
    margin: 0;
    display: flex;
    justify-content: space-between !important;

    span {
      font-size: ${themeResponsiveProperty('1.3125rem', '1.875rem')};
    }
  }
`;

const BadgesContainer = styled.div`
  position: absolute;
  inset: ${themeResponsiveProperty('.625rem auto auto .625rem', '1.25rem auto auto 1.25rem')};
  display: flex;
  flex-direction: row;
  gap: ${themeResponsiveProperty('.25rem', '.5rem')};
  font-family: 'Din-Medium', sans-serif;
`;

const Badge = styled.div`
  border-radius: .3125rem;
  background-color: #000;
  color: #FFF;
  padding: 0 .625rem;
  line-height: 1.125rem;
  font-size: .625rem;
  text-transform: uppercase;
`

const CompletionBarContainer = styled.div`
  height: ${themeResponsiveProperty('.75rem', '2rem')};
  width: 100%;
`;
